import React, { createContext, useContext, useEffect, useState } from "react";
import { getCurrentUser, getUserByLoginName } from "../Service/UserService";
import { getDevImpersonationLoginName } from "../Utility/devImpersonation";

export interface User {
  Id: number;
  FullName: string;
  Person: string;
  Active: boolean;
  Address: string;
  Email: string;
  Phone: string;
  City: string;
  Region: string;
  RegionID?: number; // Region ID for UserRegionID placeholder replacement
  Zip: string;
  Role: string;
  LoginName: string;
  WorkPhone: string | null;
  Cell: string;
  Leadership: boolean;
  Department: string;
  PersonalEmail: string;
  DontText: boolean; // Updated from number to boolean to match API
  DontEmail: boolean; // Updated from number to boolean to match API
  Supervisor: string | null;
  DateHired: string; // ISO date string
  AnalystLevel: string;
  EmpStatus: string;
  SPID: number;
  State: string;
  PBSLiveExtension: string;
  HoursRequiredperWeek: number;
  TimeZone: string;
  Country: string;
  RoleID: number;
  LastTimeEntry: string | null;
  Range: string;
  StudentAnalystLevel: string | null;
  TerminationDate: string | null;
}

// Define the context type
interface UserContextType {
  user: User | null;
  loading: boolean;
}

// Create the context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Create a provider component
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = async () => {
    try {
      const impersonateLoginName = getDevImpersonationLoginName();

      if (impersonateLoginName) {
        console.info(
          `[DEV] Impersonating "${impersonateLoginName}" (?loginAs= or REACT_APP_IMPERSONATE).`,
        );
      }

      const response = impersonateLoginName
        ? await getUserByLoginName(impersonateLoginName)
        : await getCurrentUser();

      // Handle OData response format: { "@odata.context": "...", "value": [userObject] }
      let userData: User | null = null;

      if (response.data) {
        const data = response.data as any;

        // Check if it's an OData response with value array
        if (data.value && Array.isArray(data.value) && data.value.length > 0) {
          userData = data.value[0] as User;
        }
        // Check if response.data is directly the user object
        else if (data.Id !== undefined) {
          userData = data as User;
        }
        // Check if response.data.value is directly the user object (single object, not array)
        else if (data.value && data.value.Id !== undefined) {
          userData = data.value as User;
        }
      }

      setUser(userData);
    } catch (error: any) {
      // Handle Azure AD authentication errors gracefully
      const errorMsg = error?.message || "";

      if (
        errorMsg.includes("interaction_required") ||
        errorMsg.includes("AADSTS160021") ||
        errorMsg.includes("Authentication required") ||
        errorMsg.includes("Authentication in progress")
      ) {
        console.warn(
          "User authentication required or in progress. Waiting for login...",
          error,
        );
        // Don't set an error state here - let the authentication flow complete
        // The interceptor will handle the redirect
      } else {
        console.error("Failed to fetch current user:", error);
        // For other errors, log but don't crash the app
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchCurrentUser();
    };

    fetchData();
  }, []);

  return (
    <UserContext.Provider value={{ user, loading }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
